import { memo, type ReactNode } from "react";
import { ScopeRagSummary } from "@/components/chat/ScopeRagSummary";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link } from "wouter";
import {
  ArrowLeft,
  ChevronRight,
  FileText,
  Layers,
  Link2,
  Lock,
  LockOpen,
  MapPin,
  MessagesSquare,
  Flag,
  MoreVertical,
  Music,
  PanelRight,
  Pencil,
  Share2,
  Star,
  Tags,
  Target,
  Trash2,
  X,
} from "lucide-react";
import { PersonalizationIndicator, type PersonalizationDetails } from "@/components/PersonalizationIndicator";
import { TaskMiniPanel } from "@/components/chat/TaskMiniPanel";
import { RagScopeControl } from "@/components/RagScopeSelector";
import { PendingThreadStatusBadge } from "@/components/chat/PendingThreadStatusBanner";
import { NotificationsMenuSub } from "@/components/NotificationBell";
import { useUnreadNotificationCount } from "@/hooks/useUnreadNotificationCount";
import { useToast } from "@/hooks/use-toast";
import { useMastermind } from "@/contexts/MastermindContext";
import { getTopicIcon } from "@/lib/utils";
import type { MastermindSpace, Topic, ChatThread } from "@shared/schema";
import { useAuth } from "@/contexts/AuthContext";
import { userEntityPreferencesService, navPrefsQueryKey } from "@/services/userEntityPreferencesService";
import { PRIORITY_COLORS, PRIORITY_OPTIONS, type NavPreference, type NavPriority } from "@/lib/navPrefsPure";
import { TokenSavingsChip } from "@/components/chat/TokenSavingsChip";

// 36px visual on mobile with a pseudo-element extending the touch target to 44px
// (Apple HIG / Material minimum); compact 32px on desktop where pointers are precise.
const ICON_BUTTON_CLASS =
  "relative flex-shrink-0 h-9 w-9 sm:h-8 sm:w-8 after:absolute after:-inset-1 after:content-[''] sm:after:hidden";

/**
 * Star + priority state (left-nav enhancement). Self-contained: reads/writes
 * ONLY the private user_entity_preferences relation (never shared thread rows),
 * shares the sidebar's query key so both surfaces stay in sync, optimistic
 * update with rollback + calm toast on failure.
 */
function useThreadPrefs(threadId: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: prefs = [] } = useQuery<NavPreference[]>({
    queryKey: navPrefsQueryKey(user?.id),
    queryFn: () => userEntityPreferencesService.getPreferences(),
    enabled: !!user,
    staleTime: 30000,
  });
  const pref = prefs.find((p) => p.entityType === "thread" && p.entityId === threadId);
  const isFavorite = pref?.isFavorite ?? false;
  const priority: NavPriority = pref?.priority ?? "none";

  const mutation = useMutation({
    mutationFn: (patch: { isFavorite?: boolean; priority?: NavPriority }) =>
      userEntityPreferencesService.setPreference("thread", threadId, patch),
    onMutate: async (patch) => {
      const key = navPrefsQueryKey(user?.id);
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<NavPreference[]>(key);
      queryClient.setQueryData<NavPreference[]>(key, (old = []) => {
        const rest = old.filter((p) => !(p.entityType === "thread" && p.entityId === threadId));
        const existing = old.find((p) => p.entityType === "thread" && p.entityId === threadId);
        return [...rest, {
          entityType: "thread" as const,
          entityId: threadId,
          isFavorite: patch.isFavorite ?? existing?.isFavorite ?? false,
          priority: patch.priority ?? existing?.priority ?? "none",
        }];
      });
      return { prev };
    },
    onError: (_e, _p, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(navPrefsQueryKey(user?.id), ctx.prev);
      toast({ title: "Couldn't save", description: "Your change didn't stick — please try again.", variant: "destructive" });
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: navPrefsQueryKey(user?.id) }),
  });

  return { user, isFavorite, priority, mutate: mutation.mutate };
}

/** DS EDIT 5aug26 — favorite toggle + priority submenu, embedded in the thread ⋮ menu. */
function ThreadPrefMenuItems({ threadId }: { threadId: string }) {
  const { user, isFavorite, priority, mutate } = useThreadPrefs(threadId);
  if (!user) return null;

  return (
    <>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          mutate({ isFavorite: !isFavorite });
        }}
        data-testid="button-thread-favorite"
      >
        <Star className={`h-4 w-4 mr-2 ${isFavorite ? "fill-amber-400 text-amber-400" : ""}`} />
        {isFavorite ? "Remove from favorites" : "Add to favorites"}
      </DropdownMenuItem>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger data-testid="button-thread-priority">
          <Flag
            className="h-4 w-4 mr-2"
            style={priority !== "none" ? { color: PRIORITY_COLORS[priority as keyof typeof PRIORITY_COLORS] } : undefined}
          />
          Priority{priority !== "none" ? `: ${priority}` : ""}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-40">
          {PRIORITY_OPTIONS.map((opt) => (
            <DropdownMenuItem
              key={opt.value}
              onClick={() => mutate({ priority: opt.value })}
              data-testid={`menu-priority-${opt.value}`}
            >
              <Flag
                className="h-3.5 w-3.5 mr-2"
                style={opt.value !== "none" ? { color: PRIORITY_COLORS[opt.value as keyof typeof PRIORITY_COLORS] } : undefined}
              />
              {opt.label}
              {priority === opt.value && <span className="ml-auto text-xs">✓</span>}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </>
  );
}

interface ChatHeaderProps {
  currentThread: ChatThread | null | undefined;
  currentThreadId: string | null;
  spaces: MastermindSpace[];
  spaceTopics: Topic[];
  selectedSpaceIds: string[];
  selectedTopicId: string | null;
  onClearContext: () => void;
  onBack: () => void;
  personalizationDetails: PersonalizationDetails;
  showMetadataPanel: boolean;
  setShowMetadataPanel: (open: boolean) => void;
  threadLinkingDialogOpen: boolean;
  setThreadLinkingDialogOpen: (open: boolean) => void;
  isEditingTitle: boolean;
  setIsEditingTitle: (value: boolean) => void;
  editedTitle: string;
  setEditedTitle: (value: string) => void;
  onRenameTitle: (title: string) => void;
  onLockToggle: () => void;
  onShareThread?: () => void;
  onSendToSongCreator?: () => void;
  isLockPending: boolean;
  onCopyThreadLink: () => void;
  onSummarizeArchive: () => void;
  onDeleteThread: () => void;
  hasMessages: boolean;
  hasAssistantMessages: boolean;
  pendingThreadStatus?: "local_only" | "syncing" | "verified" | "idle";
  onUpdateTags?: (tags: string[]) => void;
  /** DS EDIT 5aug26 — extra icon controls rendered in the single header row (e.g. Home's search + customize). */
  headerExtras?: ReactNode;
}

export const ChatHeader = memo(function ChatHeader(props: ChatHeaderProps) {
  const { isCollapsed, setIsCollapsed } = useMastermind();
  const unreadNotificationCount = useUnreadNotificationCount();
  const {
    currentThread,
    currentThreadId,
    spaces,
    spaceTopics,
    selectedSpaceIds,
    selectedTopicId,
    onClearContext,
    onBack,
    personalizationDetails,
    setShowMetadataPanel,
    setThreadLinkingDialogOpen,
    isEditingTitle,
    setIsEditingTitle,
    editedTitle,
    setEditedTitle,
    onRenameTitle,
    onLockToggle,
    onShareThread,
    onSendToSongCreator,
    isLockPending,
    onCopyThreadLink,
    onSummarizeArchive,
    onDeleteThread,
    hasMessages,
    pendingThreadStatus,
    onUpdateTags,
    headerExtras,
  } = props;

  const selectedTopic = selectedTopicId
    ? spaceTopics.find((t) => t.id === selectedTopicId)
    : undefined;
  const hasLocation = selectedSpaceIds.length > 0 || !!selectedTopicId;
  const threadTags: string[] = currentThread?.tags || [];

  const saveEditedTitle = () => {
    const trimmedTitle = editedTitle.trim();
    if (trimmedTitle && currentThread && trimmedTitle !== currentThread.title) {
      onRenameTitle(trimmedTitle);
    }
    setIsEditingTitle(false);
  };

  return (
    <div className="flex items-center gap-1 sm:gap-2 my-2 flex-shrink-0 pr-2 min-w-0">
      <Button
        size="icon"
        variant="ghost"
        className={ICON_BUTTON_CLASS}
        onClick={onBack}
        data-testid="button-back"
      >
        <ArrowLeft className="h-4 w-4" />
      </Button>

      {pendingThreadStatus ? (
        <PendingThreadStatusBadge status={pendingThreadStatus} />
      ) : null}

      {/* Title: fills all space between the back button and the compact icon cluster.
          DS EDIT 5aug26 — renaming opens a pop-out dialog so the FULL title is visible while editing. */}
      <div className="flex-1 min-w-0 flex items-center">
        {currentThreadId && currentThread ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="text-sm font-medium truncate min-w-0 cursor-pointer hover:text-primary transition-colors"
                onClick={() => {
                  setEditedTitle(currentThread.title || "Untitled Chat");
                  setIsEditingTitle(true);
                }}
                data-testid="text-thread-title"
              >
                {currentThread.title || "Untitled Chat"}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[min(90vw,28rem)]">
              <p className="whitespace-pre-wrap break-words">{currentThread.title || "Untitled Chat"}</p>
              <p className="text-xs text-muted-foreground">Click to rename</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className="text-sm font-medium truncate">New Chat</span>
        )}
      </div>

      {/* Rename pop-out: full title always visible while editing */}
      {currentThreadId && currentThread && (
        <Dialog
          open={isEditingTitle}
          onOpenChange={(open) => {
            if (!open) setIsEditingTitle(false);
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Rename thread</DialogTitle>
            </DialogHeader>
            <Textarea
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              rows={3}
              className="text-sm resize-none"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  saveEditedTitle();
                } else if (e.key === "Escape") {
                  setIsEditingTitle(false);
                }
              }}
              autoFocus
              data-testid="input-thread-title"
            />
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsEditingTitle(false)}
                data-testid="button-cancel-rename-title"
              >
                Cancel
              </Button>
              <Button
                onClick={saveEditedTitle}
                disabled={!editedTitle.trim()}
                data-testid="button-save-thread-title"
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
        <TokenSavingsChip />
        {headerExtras}

        <span className="hidden sm:inline-flex">
          <PersonalizationIndicator details={personalizationDetails} iconOnly className="h-8 w-8" />
        </span>

        {/* Ellipsis — DS EDIT 5aug26: tasks, location, favorite/priority, tags and knowledge
            scope all live HERE now (one clean row; red dot signals pending notifications). */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={ICON_BUTTON_CLASS}
              data-testid="button-thread-menu"
            >
              <MoreVertical className="h-4 w-4" />
              {unreadNotificationCount > 0 && (
                <span
                  className="absolute top-1 right-1 h-2 w-2 rounded-full bg-destructive pointer-events-none"
                  data-testid="dot-notification-indicator"
                />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <NotificationsMenuSub />
            <DropdownMenuSeparator />

            {/* Smart Tasks mini panel (DS EDIT 2): count, next tasks, add, suggest, link */}
            {currentThreadId && currentThread && (
              <TaskMiniPanel
                threadId={currentThreadId}
                threadTitle={currentThread.title || "Untitled Chat"}
                asSubmenu
                getThreadText={() => {
                  const msgs = (currentThread as { messages?: Array<{ role?: string; content?: unknown }> }).messages ?? [];
                  return msgs
                    .slice(-30)
                    .map((m) => `${m.role ?? "user"}: ${typeof m.content === "string" ? m.content : ""}`)
                    .join("\n");
                }}
              />
            )}

            {/* Location: Space › Topic breadcrumb */}
            {hasLocation && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger data-testid="button-location-indicator">
                  <MapPin className="h-4 w-4 mr-2" /> Location
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-64">
                  <DropdownMenuLabel>Location</DropdownMenuLabel>
                  {selectedSpaceIds.map((spaceId) => {
                    const space = spaces.find((s) => s.id === spaceId);
                    return (
                      <DropdownMenuItem asChild key={spaceId} data-testid={`menu-location-space-${spaceId}`}>
                        <Link href={`/spaces/${spaceId}`}>
                          <Target className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{space?.name || "Space"}</span>
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                  {selectedTopicId &&
                    (() => {
                      const { icon: TopicIcon, color } = getTopicIcon("folder");
                      const topicRow = (
                        <>
                          <ChevronRight className="h-3 w-3 mr-1 text-muted-foreground flex-shrink-0" />
                          <TopicIcon className={`h-4 w-4 mr-2 flex-shrink-0 ${color}`} />
                          <span className="truncate">{selectedTopic?.name || "Topic"}</span>
                        </>
                      );
                      return selectedTopic?.spaceId ? (
                        <DropdownMenuItem asChild data-testid="menu-location-topic">
                          <Link href={`/spaces/${selectedTopic.spaceId}`}>{topicRow}</Link>
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem data-testid="menu-location-topic">{topicRow}</DropdownMenuItem>
                      );
                    })()}
                  {selectedSpaceIds[0] && !selectedTopicId && (
                    <ScopeRagSummary scopeType="space" scopeId={selectedSpaceIds[0]} compact />
                  )}
                  {selectedTopicId && (
                    <ScopeRagSummary scopeType="topic" scopeId={selectedTopicId} compact />
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onClearContext} data-testid="menu-location-clear-context">
                    <X className="h-4 w-4 mr-2" /> Clear Context
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}

            {/* Favorite + priority (private per-user; writes only to user_entity_preferences) */}
            {currentThreadId && <ThreadPrefMenuItems threadId={currentThreadId} />}

            {/* Tags */}
            {currentThreadId && currentThread && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger data-testid="button-tags-indicator">
                  <Tags className="h-4 w-4 mr-2" />
                  Tags{threadTags.length > 0 ? ` (${threadTags.length})` : ""}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-64">
                  {threadTags.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 px-2 py-1.5">
                      {threadTags.map((tag, idx) => (
                        <Badge
                          key={`${tag}-${idx}`}
                          variant="secondary"
                          className="text-xs gap-1 pr-1"
                          data-testid={`badge-thread-tag-${idx}`}
                        >
                          <span className="max-w-[140px] truncate">{tag}</span>
                          {onUpdateTags && (
                            <button
                              type="button"
                              className="rounded-full hover:bg-muted-foreground/20 p-0.5"
                              onClick={() => onUpdateTags(threadTags.filter((t) => t !== tag))}
                              aria-label={`Remove tag ${tag}`}
                              data-testid={`button-remove-tag-${idx}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground italic">No tags</div>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      setShowMetadataPanel(false);
                      setThreadLinkingDialogOpen(true);
                    }}
                    data-testid="menu-manage-tags"
                  >
                    <Pencil className="h-4 w-4 mr-2" /> Manage Tags
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}

            {/* DS EDIT 5 — multi-workspace knowledge scope (pending OK on New Chat) */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="button-rag-scope">
                <Layers className="h-4 w-4 mr-2" /> Knowledge scope
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-72 p-3">
                <RagScopeControl
                  threadId={currentThreadId && currentThread ? currentThreadId : null}
                  variant="panel"
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            {currentThreadId && currentThread && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    setEditedTitle(currentThread.title || "Untitled Chat");
                    setIsEditingTitle(true);
                  }}
                  data-testid="menu-rename"
                >
                  <Pencil className="h-4 w-4 mr-2" /> Rename
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onLockToggle} disabled={isLockPending} data-testid="menu-lock-thread">
                  {currentThread?.isLocked ? (
                    <>
                      <LockOpen className="h-4 w-4 mr-2" /> Unlock Thread
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4 mr-2" /> Lock Thread
                    </>
                  )}
                </DropdownMenuItem>
                {onShareThread && (
                  <DropdownMenuItem onClick={onShareThread} data-testid="menu-share-thread">
                    <Share2 className="h-4 w-4 mr-2" /> Share Thread
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={onCopyThreadLink} data-testid="menu-copy-link">
                  <Link2 className="h-4 w-4 mr-2" /> Copy Link
                </DropdownMenuItem>
                {onSendToSongCreator && (
                  <DropdownMenuItem onClick={onSendToSongCreator} data-testid="menu-send-to-song-creator">
                    <Music className="h-4 w-4 mr-2" /> Send to Song Creator
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    setShowMetadataPanel(false);
                    setThreadLinkingDialogOpen(true);
                  }}
                  disabled={!hasMessages}
                  data-testid="menu-manage-links"
                >
                  <MessagesSquare className="h-4 w-4 mr-2" /> Manage Links
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onSummarizeArchive}
                  disabled={!hasMessages}
                  data-testid="menu-summarize-archive"
                >
                  <FileText className="h-4 w-4 mr-2 shrink-0" /> Summarize &amp; Archive
                </DropdownMenuItem>
                {selectedSpaceIds.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link href={`/spaces/${selectedSpaceIds[0]}`} data-testid="menu-back-to-space">
                        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Space
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onClearContext} data-testid="menu-clear-context">
                      <X className="h-4 w-4 mr-2" /> Clear Context
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onDeleteThread}
                  className="text-destructive focus:text-destructive"
                  data-testid="menu-delete-thread"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Delete Thread
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="ghost"
          size="icon"
          className={ICON_BUTTON_CLASS}
          onClick={() => setIsCollapsed(!isCollapsed)}
          data-testid="button-toggle-right-panel"
          title={isCollapsed ? "Show right panel" : "Hide right panel"}
        >
          <PanelRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
});
